/**
 * Session management utilities for impersonation in ben-app
 * Uses localStorage for cross-tab synchronization
 * Integrates with existing authentication patterns in store/authentication/session.js
 */
import type { User } from 'shared/types/user';
import * as session from 'store/authentication/session';

// Storage keys following ben-app patterns
const IMPERSONATION_SESSION_KEY = '$BEN$--impersonationSession';
const IMPERSONATION_STATE_KEY = '$BEN$--impersonationActive';

export interface ImpersonationSessionInfo {
  originalAdminToken: string;
  originalAdminUser: User;
  startedAt: number;
}

// Storage event listeners registry for cleanup (kept for future extensibility)
// const storageListeners = new Set<(e: StorageEvent) => void>();

/**
 * Get current impersonation session data from localStorage
 * Returns null if not impersonating
 */
export function getCurrentImpersonationSession(): ImpersonationSessionInfo | null {
  const stored = localStorage.getItem(IMPERSONATION_SESSION_KEY);
  if (!stored) return null;

  try {
    return JSON.parse(stored);
  } catch {
    localStorage.removeItem(IMPERSONATION_SESSION_KEY);
    return null;
  }
}

/**
 * Check if currently impersonating a user
 */
export function isImpersonating(): boolean {
  // Check both the state flag and session data for consistency
  const stateFlag = localStorage.getItem(IMPERSONATION_STATE_KEY) === 'true';
  const sessionExists = Boolean(getCurrentImpersonationSession());
  // Use AND to ensure both indicators are present for valid impersonation
  return stateFlag && sessionExists;
}

/**
 * Get the original admin token from the impersonation session
 * Used for API calls that require admin privileges during impersonation
 */
export function getOriginalAdminToken(): string | null {
  const impersonationSession = getCurrentImpersonationSession();
  return impersonationSession?.originalAdminToken || null;
}

/**
 * Get the original admin user info
 */
export function getOriginalAdminUser(): User | null {
  const impersonationSession = getCurrentImpersonationSession();
  return impersonationSession?.originalAdminUser || null;
}

/**
 * Check if the impersonation session is valid and not expired
 * Currently there's no expiration logic, but this provides a place to add it
 */
export function isValidImpersonationSession(): boolean {
  const impersonationSession = getCurrentImpersonationSession();

  // Could add expiration logic here in the future
  // const now = Date.now();
  // const maxAge = 8 * 60 * 60 * 1000; // 8 hours
  // return (now - impersonationSession.startedAt) < maxAge;

  return Boolean(impersonationSession);
}

/**
 * Clear impersonation session and restore original admin session
 * This function integrates with ben-app's session management
 */
export function endImpersonationSession(): boolean {
  const impersonationSession = getCurrentImpersonationSession();

  if (!impersonationSession) {
    return false;
  }

  // Restore original admin session using ben-app's session methods
  // Note: This assumes the session.create method exists and works as expected
  try {
    // Get current permissions and versioning to preserve them if needed
    const currentPermissions = session.getPermissions();
    const currentVersioning = session.getVersioning();

    // Restore original admin session
    session.create({
      user: impersonationSession.originalAdminUser,
      accessToken: impersonationSession.originalAdminToken,
      permissions: currentPermissions,
      versioning: currentVersioning,
    });

    // Clear impersonation session from localStorage
    localStorage.removeItem(IMPERSONATION_SESSION_KEY);
    localStorage.removeItem(IMPERSONATION_STATE_KEY);

    return true;
  } catch (error) {
    // Log error in development only
    if (process.env.NODE_ENV === 'development') {
      // Only log safe, whitelisted information
      const safeError = {
        error: 'Failed to end impersonation session',
        code: (error as { code?: string })?.code,
        timestamp: new Date().toISOString(),
        // Never log: tokens, user data, stack traces, or error messages with sensitive data
      };
      // eslint-disable-next-line no-console
      console.error('[Impersonation Error]', safeError);
    }
    return false;
  }
}

/**
 * Initialize impersonation session
 * This function should be called when impersonation starts
 */
export function initializeImpersonationSession(
  originalAdminToken: string,
  originalAdminUser: User,
): void {
  const sessionData: ImpersonationSessionInfo = {
    originalAdminToken,
    originalAdminUser,
    startedAt: Date.now(),
  };

  localStorage.setItem(IMPERSONATION_SESSION_KEY, JSON.stringify(sessionData));
  localStorage.setItem(IMPERSONATION_STATE_KEY, 'true');
}

/**
 * Cleanup function to remove impersonation session data
 * Should be called on logout or session cleanup
 */
export function cleanupImpersonationSession(): void {
  localStorage.removeItem(IMPERSONATION_SESSION_KEY);
  localStorage.removeItem(IMPERSONATION_STATE_KEY);
}

/**
 * Listen for impersonation state changes across tabs
 * Returns a cleanup function to remove the listener
 */
export function onImpersonationChange(
  callback: (isImpersonating: boolean) => void,
): () => void {
  const listener = (e: StorageEvent) => {
    // Only react to changes from other tabs
    if (e.storageArea !== localStorage) return;

    if (
      e.key === IMPERSONATION_STATE_KEY ||
      e.key === IMPERSONATION_SESSION_KEY
    ) {
      const impersonating = isImpersonating();
      callback(impersonating);
    }
  };

  window.addEventListener('storage', listener);

  // Return cleanup function
  return () => {
    window.removeEventListener('storage', listener);
  };
}
